import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { usePageTitle } from '../hooks/usePageTitle';
import { supabase } from '../supabaseClient';
import {
  getArticleContent, getAdditionalArticles, getMoreArticles,
  getScaleArticles, getLLMArticles, getHealthcareArticles,
  getTechDebtArticles, getPrototypeArticles,
} from '../data/blogArticlesData';
import './BlogArticle.css';

function getStaticArticle(slug) {
  return (
    getArticleContent(slug) ||
    getAdditionalArticles(slug) ||
    getMoreArticles(slug) ||
    getScaleArticles(slug) ||
    getLLMArticles(slug) ||
    getHealthcareArticles(slug) ||
    getTechDebtArticles(slug) ||
    getPrototypeArticles(slug)
  );
}

function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined); // undefined=loading, null=not found
  usePageTitle(article?.title ?? 'Blog');

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;

    const staticArticle = getStaticArticle(slug);
    if (staticArticle) {
      setArticle(staticArticle);
      return;
    }

    supabase
      .from('blogs')
      .select('*')
      .eq('id', slug)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (data && !error) {
          setArticle({
            title:    data.title,
            category: null,
            author:   data.author,
            date:     data.date,
            readTime: null,
            content:  `<div class="article-intro"><p>${data.content || ''}</p></div>`,
          });
        } else {
          setArticle(null);
        }
      })
      .catch(() => {
        if (!cancelled) setArticle(null);
      });

    return () => { cancelled = true; };
  }, [slug]);

  if (article === undefined) {
    return (
      <div className="blog-article">
        <div className="article-container" style={{ padding: '6rem 0', textAlign: 'center', color: '#64748b' }}>
          Loading…
        </div>
      </div>
    );
  }

  if (article === null) {
    return (
      <div className="blog-article">
        <div className="article-container">
          <Link to="/blog" className="back-link">← Back to Blog</Link>
          <h1 style={{ color: '#f1f5f9', marginTop: '2rem' }}>Article Not Found</h1>
          <p style={{ color: '#94a3b8' }}>
            This article doesn't exist or may have been moved.{' '}
            <Link to="/blog" style={{ color: '#667eea' }}>Browse all articles →</Link>
          </p>
        </div>
      </div>
    );
  }

  const safeHtml = DOMPurify.sanitize(article.content);

  return (
    <div className="blog-article">
      <div className="article-header">
        <div className="article-container">
          <Link to="/blog" className="back-link">← Back to Blog</Link>
          <div className="article-meta">
            {article.category && <span className="article-category">{article.category}</span>}
            <span className="article-date">{article.date}</span>
            {article.readTime && <span className="article-read-time">{article.readTime}</span>}
          </div>
          <h1 className="article-main-title">{article.title}</h1>
          {article.author && <div className="article-author">By {article.author}</div>}
        </div>
      </div>

      <div className="article-body">
        <div className="article-container">
          <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
        </div>
      </div>
    </div>
  );
}

export default BlogArticle;
